import { LogOut, ShieldAlert, User } from 'lucide-react';
import { Link } from 'react-router-dom';

import Logo from '@/components/common/Logo';
import { Badge, Button, Card } from '@/components/ui';
import useAuth from '@/hooks/useAuth';
import { departmentForJobRole, ROUTES } from '@/utils/constants';

/**
 * Where an approved account lands when its jobRole maps to no workspace - a
 * registration that predates the current role list, or a role an admin has
 * since retired. Replaces the old /dashboard placeholder that EVERY newly
 * approved employee used to land on regardless of their role.
 *
 * Standalone (no sidebar) on purpose: this user has no workspace to show a nav
 * for. The chrome mirrors the admin console's card/typography tokens so it does
 * not read as a different product.
 */
const NoWorkspacePage = () => {
  const { user, logout } = useAuth();
  const department = departmentForJobRole(user?.jobRole);

  return (
    <div className="min-h-screen bg-cream">
      <header className="sticky top-0 z-10 border-b border-line bg-white shadow-sm">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Logo />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              void logout();
            }}
            className="flex items-center gap-1.5"
          >
            <LogOut size={16} />
            Sign out
          </Button>
        </div>
      </header>

      <main className="mx-auto flex max-w-7xl justify-center px-4 py-12 sm:px-6 sm:py-20 lg:px-8">
        <Card padding="lg" className="flex w-full max-w-lg flex-col items-center gap-4 text-center">
          <span className="grid h-16 w-16 place-items-center rounded-2xl bg-warning-tint">
            <ShieldAlert className="h-8 w-8 text-ink/70" />
          </span>

          <div>
            <h1 className="text-2xl font-extrabold tracking-tight text-ink">
              No workspace assigned
            </h1>
            <p className="mt-2 text-sm text-ink/60">
              Your account is active, but the job role on it does not open any workspace. Please
              contact your administrator to have the correct role assigned.
            </p>
          </div>

          <dl className="w-full rounded-xl border border-line bg-cream/60 px-4 py-3 text-left">
            <div className="flex items-center justify-between gap-3 py-1">
              <dt className="text-xs font-semibold uppercase tracking-wider text-ink/40">Name</dt>
              <dd className="text-sm font-semibold text-ink">{user?.name ?? '-'}</dd>
            </div>
            <div className="flex items-center justify-between gap-3 py-1">
              <dt className="text-xs font-semibold uppercase tracking-wider text-ink/40">
                Job role
              </dt>
              <dd>
                <Badge variant={user?.jobRole ? 'info' : 'neutral'}>
                  {user?.jobRole ?? 'Not set'}
                </Badge>
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3 py-1">
              <dt className="text-xs font-semibold uppercase tracking-wider text-ink/40">
                Department
              </dt>
              <dd className="text-sm font-semibold text-ink">{department ?? '-'}</dd>
            </div>
          </dl>

          <Button asChild variant="outline" className="mt-1 gap-1.5">
            <Link to={ROUTES.PROFILE}>
              <User className="h-4 w-4" />
              View my profile
            </Link>
          </Button>
        </Card>
      </main>
    </div>
  );
};

export default NoWorkspacePage;
